/**
 * TaxableValueInputsTestPage - Simplified Testing Harness
 *
 * Purpose: Test core taxable value validation and conversion logic
 *
 * Test Flow:
 * 1. Component displays with empty initial state
 * 2. User can enter taxable value and growth rates
 * 3. Click "Validate" to verify validation and conversion logic
 * 4. See pass/fail results
 *
 * Note: No persistence - refreshing resets to empty state
 *
 * Validates:
 * - Form → engine format conversion (0-100% → 0-1 decimal)
 * - Validation rules (using TaxableValueBuilder.validateInputs)
 * - Growth rate constraints (0-100%)
 * - Initial taxable value constraints (> 0)
 */

import { Badge, Box, Button, Code, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';

import { TaxableValueBuilder } from '~/builders/taxable-value/TaxableValueBuilder';
import {
  convertToEngineFormat,
  TaxableValueInputs,
} from '~/builders/taxable-value/TaxableValueInputs';
import { SidebarProvider } from '~/components/layout/shared/SidebarContext';
import type { TaxValueInputsForm } from '~/types';

interface TestAssertion {
  name: string;
  actual: string;
  expected: string;
  passed: boolean;
}

interface TestResult {
  name: string;
  passed: boolean;
  assertions: TestAssertion[];
}

export const TaxableValueInputsTestPage = () => {
  const [values, setValues] = useState<TaxValueInputsForm>({
    initialTaxableValue: '',
    startYearGrowthRate: '',
    fiveYearGrowthRate: '',
    twentyYearGrowthRate: '',
  });

  const [testResults, setTestResults] = useState<TestResult[]>([]);

  const validateCurrentState = () => {
    // Check if all fields are filled
    if (
      !values.initialTaxableValue?.trim() ||
      !values.startYearGrowthRate?.trim() ||
      !values.fiveYearGrowthRate?.trim() ||
      !values.twentyYearGrowthRate?.trim()
    ) {
      alert('Please fill in all fields before validating');
      return;
    }

    const assertions: TestAssertion[] = [];

    // 1. Test Form → Engine Format Conversion
    const engineFormat = convertToEngineFormat(values);
    const startYearRateDecimal = parseFloat(values.startYearGrowthRate) / 100;
    const fiveYearRateDecimal = parseFloat(values.fiveYearGrowthRate) / 100;
    const twentyYearRateDecimal = parseFloat(values.twentyYearGrowthRate) / 100;

    assertions.push({
      name: 'Start year growth rate conversion (form 0-100% → engine 0-1)',
      actual: engineFormat.startingYearGrowthRate.toFixed(4),
      expected: startYearRateDecimal.toFixed(4),
      passed: Math.abs(engineFormat.startingYearGrowthRate - startYearRateDecimal) < 0.0001,
    });

    assertions.push({
      name: 'Five year growth rate conversion (form 0-100% → engine 0-1)',
      actual: engineFormat.fiveYearRate.toFixed(4),
      expected: fiveYearRateDecimal.toFixed(4),
      passed: Math.abs(engineFormat.fiveYearRate - fiveYearRateDecimal) < 0.0001,
    });

    assertions.push({
      name: 'Twenty year growth rate conversion (form 0-100% → engine 0-1)',
      actual: engineFormat.twentyYearRate.toFixed(4),
      expected: twentyYearRateDecimal.toFixed(4),
      passed: Math.abs(engineFormat.twentyYearRate - twentyYearRateDecimal) < 0.0001,
    });

    // 2. Test Validation Rules (using TaxableValueBuilder)
    const validation = TaxableValueBuilder.validateInputs(engineFormat);
    const hasValidationErrors = validation.errors.length > 0;

    assertions.push({
      name: 'Validation passes (no errors)',
      actual: hasValidationErrors ? `Failed: ${validation.errors.join(', ')}` : 'Passed',
      expected: 'Passed',
      passed: !hasValidationErrors,
    });

    // 3. Test Growth Rate Constraints (0-100%)
    const startYearRate = parseFloat(values.startYearGrowthRate);
    const fiveYearRate = parseFloat(values.fiveYearGrowthRate);
    const twentyYearRate = parseFloat(values.twentyYearGrowthRate);

    assertions.push({
      name: 'Start year growth rate within bounds (0-100%)',
      actual: `${startYearRate}%`,
      expected: '0-100%',
      passed: startYearRate >= 0 && startYearRate <= 100,
    });

    assertions.push({
      name: 'Five year growth rate within bounds (0-100%)',
      actual: `${fiveYearRate}%`,
      expected: '0-100%',
      passed: fiveYearRate >= 0 && fiveYearRate <= 100,
    });

    assertions.push({
      name: 'Twenty year growth rate within bounds (0-100%)',
      actual: `${twentyYearRate}%`,
      expected: '0-100%',
      passed: twentyYearRate >= 0 && twentyYearRate <= 100,
    });

    // 4. Test Initial Taxable Value Constraints (> 0)
    const initialValue = parseFloat(values.initialTaxableValue.replace(/[^\d.-]/g, '')) || 0;
    assertions.push({
      name: 'Initial taxable value > 0',
      actual: `$${initialValue.toLocaleString()}`,
      expected: '> $0',
      passed: initialValue > 0,
    });

    const testResult: TestResult = {
      name: 'Taxable Value Inputs Validation',
      passed: assertions.every((a) => a.passed),
      assertions,
    };

    setTestResults([testResult]);
  };

  return (
    <Box p={8} maxW="1400px" mx="auto">
      <VStack align="stretch" gap={8}>
        {/* Header */}
        <Box>
          <Heading size="xl" mb={2}>
            TaxableValueInputs - Test Page
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Enter taxable value and growth rates, then click Validate to verify validation and
            conversion logic.
          </Text>
        </Box>

        {/* Component Under Test */}
        <Box>
          <Heading size="md" mb={4}>
            Component
          </Heading>
          <SidebarProvider>
            <TaxableValueInputs values={values} onUpdate={setValues} showValidation={false} />
          </SidebarProvider>
        </Box>

        {/* Validate Button */}
        <HStack justify="flex-end" gap={4}>
          <Button
            size="lg"
            onClick={() =>
              setValues({
                initialTaxableValue: '',
                startYearGrowthRate: '',
                fiveYearGrowthRate: '',
                twentyYearGrowthRate: '',
              })
            }
            colorPalette="gray"
          >
            Reset to Empty
          </Button>
          <Button size="lg" onClick={validateCurrentState} colorPalette="green">
            ✓ Validate Calculations
          </Button>
        </HStack>

        {/* Test Results */}
        {testResults.length > 0 && (
          <Box>
            <Heading size="md" mb={4}>
              Validation Results
            </Heading>
            <VStack align="stretch" gap={4}>
              {testResults.map((result) => (
                <Box
                  key={result.name}
                  p={4}
                  bg={result.passed ? 'green.50' : 'red.50'}
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderColor={result.passed ? 'green.500' : 'red.500'}
                >
                  <HStack mb={3}>
                    <Badge colorScheme={result.passed ? 'green' : 'red'} fontSize="sm">
                      {result.passed ? '✅ ALL CHECKS PASSED' : '❌ SOME CHECKS FAILED'}
                    </Badge>
                    <Text fontWeight="bold">{result.name}</Text>
                  </HStack>
                  <VStack align="flex-start" gap={2} fontSize="sm">
                    {result.assertions.map((assertion) => (
                      <HStack key={assertion.name} fontFamily="mono" fontSize="xs">
                        <Text>{assertion.passed ? '✅' : '❌'}</Text>
                        <Text fontWeight="medium" minW="280px">
                          {assertion.name}:
                        </Text>
                        <Text color={assertion.passed ? 'green.700' : 'red.700'}>
                          {assertion.actual}
                        </Text>
                        {!assertion.passed && (
                          <>
                            <Text color="gray.500">(expected:</Text>
                            <Text color="gray.700">{assertion.expected})</Text>
                          </>
                        )}
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {/* Reference Documentation */}
        <Box
          p={6}
          bg="blue.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="blue.500"
          mt={8}
        >
          <Heading size="sm" mb={4}>
            📘 Taxable Value Inputs Reference
          </Heading>
          <Text fontSize="sm" mb={4} color="gray.700">
            How form values are converted and validated:
          </Text>
          <VStack align="stretch" gap={4} fontSize="sm">
            {/* Growth Rate Tier System */}
            <Box>
              <Text fontWeight="bold" mb={2} color="blue.700">
                Growth Rate Tier System
              </Text>
              <Code display="block" p={3} fontSize="xs" whiteSpace="pre" borderRadius="md">
                {`Three-tier system for conservative projections:

1. Starting Year (Year 0): Initial growth rate
   └─ Example: 2.5% → Used for first year

2. Five Year Rate (Years 1-5): Near-term growth assumption
   └─ Example: 2.0% → Used for years 1-5

3. Twenty Year Rate (Years 6+): Long-term growth assumption
   └─ Example: 1.5% → Used for years 6+

All rates are percentages in form (0-100%)
Converted to decimals in engine (0-1)`}
              </Code>
            </Box>

            {/* Conversion Formula */}
            <Box>
              <Text fontWeight="bold" mb={2} color="blue.700">
                Form → Engine Format Conversion
              </Text>
              <Code display="block" p={3} fontSize="xs" whiteSpace="pre" borderRadius="md">
                {`Form Format (UI):
├─ initialTaxableValue: "$1,000,000" (formatted currency string)
├─ startYearGrowthRate: "2.5" (0-100%)
├─ fiveYearGrowthRate: "2.0" (0-100%)
└─ twentyYearGrowthRate: "1.5" (0-100%)

Engine Format (calculations):
├─ startingYearTaxableValue: 1000000 (number)
├─ startingYearGrowthRate: 0.025 (decimal, 0-1)
├─ fiveYearRate: 0.02 (decimal, 0-1)
└─ twentyYearRate: 0.015 (decimal, 0-1)

Conversion:
├─ Currency: Strip formatting → parseFloat
└─ Percentages: formValue / 100`}
              </Code>
            </Box>

            {/* Validation Rules */}
            <Box>
              <Text fontWeight="bold" mb={2} color="blue.700">
                Validation Rules
              </Text>
              <Code display="block" p={3} fontSize="xs" whiteSpace="pre" borderRadius="md">
                {`1. Initial Taxable Value:
   └─ Must be > 0

2. Growth Rates (all three):
   └─ Must be > 0 and <= 100% (form)
   └─ Must be > 0 and <= 1 (engine)

3. All fields required:
   └─ initialTaxableValue
   └─ startYearGrowthRate
   └─ fiveYearGrowthRate
   └─ twentyYearGrowthRate`}
              </Code>
            </Box>

            {/* Example */}
            <Box
              p={3}
              bg="yellow.50"
              borderRadius="md"
              borderLeft="3px solid"
              borderColor="yellow.500"
            >
              <Text fontWeight="bold" fontSize="xs" mb={1} color="yellow.800">
                💡 Example Test Case
              </Text>
              <Text fontSize="xs" color="yellow.900" whiteSpace="pre">
                {`Form Input:
├─ Initial Value: $1,000,000
├─ Start Year Rate: 2.5%
├─ Five Year Rate: 2.0%
└─ Twenty Year Rate: 1.5%

Expected Engine Format:
├─ startingYearTaxableValue: 1000000
├─ startingYearGrowthRate: 0.025
├─ fiveYearRate: 0.02
└─ twentyYearRate: 0.015

All validations should pass ✅`}
              </Text>
            </Box>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};
